/**
 * sentryInit — bootstrap @sentry/react for browser error tracking.
 *
 * Import this as the FIRST line of each entry point (indexLanding.tsx, indexAdmin.tsx)
 * before importing React/App, so Sentry can patch fetch/XHR/console early.
 *
 * Configuration via __CLIENT_ENV__ (injected by rspack DefinePlugin at build time):
 *   SENTRY_DSN                 — required to enable Sentry (otherwise no-op)
 *   SENTRY_ENVIRONMENT         — defaults to NODE_ENV
 *   SENTRY_RELEASE             — git SHA or build ID; helps map errors to deploys
 *   SENTRY_TRACES_SAMPLE_RATE  — 0.0 to 1.0, default 0.1
 *
 * SSR-safe: no-op when `window` is undefined.
 */

declare var __CLIENT_ENV__: Record<string, string> | undefined;

function getEnv(key: string): string | undefined {
	if (typeof __CLIENT_ENV__ !== "undefined" && __CLIENT_ENV__) {
		const v = __CLIENT_ENV__[key];
		if (v) return String(v);
	}
	if (typeof process !== "undefined" && process.env && process.env[key]) {
		return String(process.env[key]);
	}
	return undefined;
}

const dsn = (getEnv("SENTRY_DSN") || "").trim();

if (typeof window !== "undefined" && dsn) {
	// eslint-disable-next-line @typescript-eslint/no-var-requires
	const Sentry = require("@sentry/react");

	Sentry.init({
		dsn,
		environment:
			getEnv("SENTRY_ENVIRONMENT") || getEnv("NODE_ENV") || "development",
		release: getEnv("SENTRY_RELEASE") || undefined,

		integrations: [Sentry.browserTracingIntegration()],

		tracesSampleRate:
			Number(getEnv("SENTRY_TRACES_SAMPLE_RATE")) || 0.1,

		sendDefaultPii: false,

		beforeSend(event, hint) {
			const ex = hint && (hint.originalException as any);
			if (ex && typeof ex === "object") {
				const msg = String(ex.message || "");
				if (msg.includes("ResizeObserver loop limit exceeded")) return null;
				if (msg.includes("Non-Error promise rejection captured")) return null;
				if (msg.includes("AbortError")) return null;
			}
			return event;
		},

		maxBreadcrumbs: 50,
	});

	// eslint-disable-next-line no-console
	console.log(
		`[sentry] initialized · env=${getEnv("SENTRY_ENVIRONMENT") || getEnv("NODE_ENV") || "development"} · sample=${Number(getEnv("SENTRY_TRACES_SAMPLE_RATE")) || 0.1}`,
	);
}
